import * as React from 'react'
import { useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ErrorBoundary } from '@/modules/shared/components/ErrorBoundary'
import { ErrorFallback } from '@/modules/shared/components/ErrorFallback'
import {
  useLocalize,
} from '@/modules/shared/context/localize/LocalizeContext'
import { AppLayoutDahboardWithNav } from '@/modules/shared/layouts/AppLayoutDahboardWithNav'
import { HeadBar, SubNavigationBar } from '@/modules/shared/components'
import { SettingsIcon } from '@/app/features/icons/Settings'
import { SubNavigationButton } from '@/modules/shared/components/SubNavigationButton'
import { Loader } from '@/ui'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import {
  ServiceAccount,
  useQueryMyServiceAccounts,
  useQueryServiceAccounts,
} from '@/modules/shared/query-client/useServiceAccountAdmin'
import { ServiceAccountUsersPanel } from '@/modules/shared/components/serviceAccount/ServiceAccountRoster'
import { ServiceAccountAccessPanel } from '@/modules/shared/components/serviceAccount/access/ServiceAccountAccessPanel'
import { EditCompanyForm } from '@/modules/addCompany/components/EditCompanyForm'
import { useUser } from '@/modules/shared/context/UserContext'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { CompanyType } from '@/app/constants/companyType'
import { AdminCompaniesPage } from '@/modules/adminPanel/companies/AdminCompaniesPage'
import { CompanyRolesView } from '@/modules/users/pages/roles/RolesPage'

// "Firmy" is the admin panel's company table scoped to the account.
type Tab = 'companies' | 'users' | 'access' | 'roles' | 'profile'
const TABS: Tab[] = ['companies', 'users', 'access', 'roles', 'profile']

// Owner-facing panel for a service account (konto usługowe). Reached at
// /et/company/:companyId/serviceAccount, where :companyId is any scope company the user is in
// (drives the outer layout/sidebar); the content is the user's OWN account(s) from
// /service-accounts/mine. Members see it read-only; owners manage the roster and edit the
// account company's profile.
//
// Styled to mirror the driver module shell: a left list panel (the account's companies) plus a
// framed content area with a top SubNavigationBar.
const sameId = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

const ServiceAccountOwnerPanelImpl: React.FC = () => {
  const { t } = useLocalize()
  const { companyId } = useParams<{ companyId: string }>()
  const { isAdmin } = useUser()
  const { data, isLoading, error } = useQueryMyServiceAccounts()
  const mine = useMemo(() => data ?? [], [data])

  const companiesQuery = useQueryCompanies({ includeDeleted: false })
  const urlIsServiceCompany = (companiesQuery.data ?? []).some(
    (c) => sameId(c.id, companyId) && c.type === CompanyType.SERVICE,
  )

  // Reached at /et/company/:companyId/serviceAccount. When that company is itself a konto
  // usługowe the user is not necessarily on its roster — a global admin who picked somebody
  // else's umbrella isn't — and /service-accounts/mine would then show a different account
  // than the URL names, or nothing at all. Pull that one account from the admin listing.
  const adminFallbackEnabled =
    isAdmin() &&
    urlIsServiceCompany &&
    !isLoading &&
    !mine.some((a) => sameId(a.id, companyId))
  const adminAccounts = useQueryServiceAccounts({ enabled: adminFallbackEnabled })

  const accounts = useMemo(() => {
    if (!adminFallbackEnabled) return mine
    const fromAdmin = (adminAccounts.data ?? [])
      .filter((a) => sameId(a.id, companyId))
      .map((a) => ({ ...a, isOwner: true }))
    return [...fromAdmin, ...mine]
  }, [mine, adminFallbackEnabled, adminAccounts.data, companyId])

  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Without the companyId match the panel would open on accounts[0] — a different account
  // than the one the URL (and the sidebar's company picker) says you are looking at.
  const selected = useMemo<ServiceAccount | null>(
    () =>
      accounts.find((a) => a.id === selectedId) ??
      accounts.find((a) => sameId(a.id, companyId)) ??
      accounts[0] ??
      null,
    [accounts, selectedId, companyId],
  )

  const frameClasses =
    'tw-flex tw-flex-[1] tw-text-content-base tw-bg-base-100 tw-min-w-0 tw-max-w-full tw-overflow-hidden tw-mt-1 tw-mb-1 tw-rounded-xl tw-border tw-border-base-300 tw-h-[calc(100vh-8px)]'

  if (isLoading || (adminFallbackEnabled && adminAccounts.isLoading))
    return (
      <div className={frameClasses}>
        <Loader fill />
      </div>
    )

  if (error) {
    return (
      <div className={frameClasses}>
        <div className="tw-p-6 tw-text-sm tw-text-red-600">
          {t('serviceAccount.load-error')}
        </div>
      </div>
    )
  }

  if (!selected) {
    return (
      <div className={frameClasses}>
        <div className="tw-p-6 tw-text-sm tw-text-gray-500">
          {t('serviceAccount.no-account')}
        </div>
      </div>
    )
  }

  return (
    <div className={frameClasses}>
      <AccountContent
        key={selected.id}
        account={selected}
        accounts={accounts}
        onSelectAccount={setSelectedId}
      />
    </div>
  )
}

// Tabs + main content column. Keyed on the account id by the parent so switching accounts resets
// the tab state.
const AccountContent: React.FC<{
  account: ServiceAccount
  accounts: ServiceAccount[]
  onSelectAccount: (id: string) => void
}> = ({ account, accounts, onSelectAccount }) => {
  const { t } = useLocalize()
  const [searchParams, setSearchParams] = useSearchParams()
  const isOwner = account.isOwner === true
  const requested = searchParams.get('tab') as Tab
  // „Osoby w firmach” tylko dla właściciela - członek z takim linkiem ląduje na „Firmach”.
  const tab: Tab =
    TABS.includes(requested) && (requested !== 'access' || isOwner) ? requested : 'companies'
  const setTab = (next: Tab) =>
    setSearchParams(
      (prev) => {
        prev.set('tab', next)
        return prev
      },
      { replace: true },
    )
  // "Anuluj" w profilu wraca do zapisanych danych: nowy klucz = świeży formularz.
  const [profileFormKey, setProfileFormKey] = useState(0)
  const [actionError, setActionError] = useState<string | null>(null)

  return (
    <>
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-x-auto tw-overflow-y-hidden">
      <div className="tw-flex tw-min-h-0 tw-min-w-[900px] tw-flex-1 tw-flex-col">
        <AccountHeader
          account={account}
          accounts={accounts}
          onSelectAccount={onSelectAccount}
        />
        <SubNavigationBar>
          <SubNavigationButton
            isActive={tab === 'companies'}
            onClick={() => setTab('companies')}
          >
            {t('serviceAccount.companies-header')} ({account.children.length})
          </SubNavigationButton>
          <SubNavigationButton isActive={tab === 'users'} onClick={() => setTab('users')}>
            {t('serviceAccount.tab-users')}
          </SubNavigationButton>
          {isOwner && (
            <SubNavigationButton isActive={tab === 'access'} onClick={() => setTab('access')}>
              Osoby w firmach
            </SubNavigationButton>
          )}
          <SubNavigationButton isActive={tab === 'roles'} onClick={() => setTab('roles')}>
            {t('usersModule.rolesTab.tabList')}
          </SubNavigationButton>
          <SubNavigationButton isActive={tab === 'profile'} onClick={() => setTab('profile')}>
            {t('serviceAccount.tab-profile')}
          </SubNavigationButton>
        </SubNavigationBar>

        <main className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden">
          {actionError && (
            <div className="tw-mx-5 tw-mt-4 tw-rounded tw-bg-red-100 tw-px-3 tw-py-2 tw-text-sm tw-text-red-800">
              {actionError}
            </div>
          )}

          {/* The users roster runs edge-to-edge (its own action bar + table span full width),
              matching usersModule; the other tabs keep the comfortable page padding. */}
          {tab === 'companies' ? (
            <AdminCompaniesPage
              currentCompanyId={account.id}
              embedded
              readOnly={!isOwner}
            />
          ) : tab === 'access' ? (
            <ServiceAccountAccessPanel account={account} />
          ) : tab === 'roles' ? (
            <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col">
              <CompanyRolesView companyId={account.id} />
            </div>
          ) : tab === 'users' ? (
            <ServiceAccountUsersPanel
              account={account}
              onError={setActionError}
              readOnly={!isOwner}
            />
          ) : (
            <div className="tw-px-5 tw-py-4">
              {tab === 'profile' &&
                (isOwner ? (
                  <EditCompanyForm
                    key={profileFormKey}
                    editedCompanyId={account.id}
                    onSuccess={() => setActionError(null)}
                    onCancel={() => setProfileFormKey((k) => k + 1)}
                  />
                ) : (
                  <div className="tw-rounded tw-border tw-border-gray-200 tw-p-4 tw-text-sm tw-text-gray-600">
                    <div className="tw-mb-1 tw-font-medium tw-text-gray-900">
                      {account.companyName}
                    </div>
                    <div>
                      {account.nip
                        ? `${t('serviceAccount.nip')}: ${account.nip}`
                        : t('serviceAccount.no-nip')}
                    </div>
                    <div className="tw-mt-2 tw-text-xs tw-text-gray-500">
                      {t('serviceAccount.profile-owner-only')}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </main>
      </div>
      </div>
    </>
  )
}

// Same head bar as "Mój profil" / "Moja subskrypcja" (company picker + title); the
// account's NIP, the viewer's role and the account switcher sit on the right.
const AccountHeader: React.FC<{
  account: ServiceAccount
  accounts: ServiceAccount[]
  onSelectAccount: (id: string) => void
}> = ({ account, accounts, onSelectAccount }) => {
  const { t } = useLocalize()
  const isOwner = account.isOwner === true
  return (
    <HeadBar
      title={t('serviceAccount.title')}
      icon={<SettingsIcon />}
      actions={
        <div className="tw-flex tw-items-center tw-gap-4">
          <span className="tw-text-sm tw-text-gray-500">
            {account.nip
              ? `${t('serviceAccount.nip')}: ${account.nip}`
              : t('serviceAccount.no-nip')}{' '}
            ·{' '}
            <span className={isOwner ? 'tw-text-blue-700' : 'tw-text-gray-600'}>
              {isOwner
                ? t('serviceAccount.role-owner')
                : t('serviceAccount.role-member-preview')}
            </span>
          </span>
          {accounts.length > 1 && (
            <Dropdown
              className="tw-w-64"
              compact
              value={account.id}
              onChange={(value: string) => onSelectAccount(value)}
              options={accounts.map((a) => ({
                label: a.companyName ?? '—',
                value: a.id,
              }))}
            />
          )}
        </div>
      }
    />
  )
}

export const ServiceAccountOwnerModule: React.FC = () => (
  <ErrorBoundary fallback={(e) => <ErrorFallback error={e.error} onRetry={e.reset} />}>
    <AppLayoutDahboardWithNav>
      <ServiceAccountOwnerPanelImpl />
    </AppLayoutDahboardWithNav>
  </ErrorBoundary>
)
